import { useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import type { FeatureSlide } from "@/data/interaktive-tourenplanung-content";


export function FeatureSlider({
  items,
  eyebrow = "Aus der Praxis gedacht",
  heading = "WEIL KEIN DISPO-ALLTAG NACH STANDARD LÄUFT",
  intro = "Operative Sonderfälle, eine Vielzahl an Planungsrestriktionen, feste Gebietslogiken oder unvollständige Planungsdaten: In der täglichen Disposition entscheiden oft die Details. Smartlane ist genau für diese Realität entwickelt und berücksichtigt zahlreiche Anforderungen, die im Planungsalltag eine Rolle spielen. Einige Beispiele:",
  footer,
}: {

  items: FeatureSlide[];
  eyebrow?: string;
  heading?: string;
  intro?: string;
  /** Optionaler Inhalt unter der Funktionsliste, z. B. ein Download-Button. */
  footer?: ReactNode;
}) {
  const [active, setActive] = useState(0);
  const current = items[active];

  if (!current) return null;

  return (
    <section
      id="neue-funktionen"
      className="relative scroll-mt-24 py-12 md:py-16 xl:py-24"
      aria-labelledby="feature-slider-heading"
    >
      <div className="container-wide">
        <div className="max-w-4xl">
          <p className="eyebrow">{eyebrow}</p>
          <h2 id="feature-slider-heading" className="mt-3 text-display-md text-rich-black">
            {heading}
          </h2>
          <p className="copy mt-5 max-w-3xl text-base leading-relaxed text-rich-black/70 md:text-lg">{intro}</p>
        </div>

        <div className="mt-8 grid min-w-0 items-start gap-6 lg:grid-cols-[minmax(15rem,0.72fr)_minmax(0,1.55fr)] lg:gap-8 xl:gap-14">
          <div role="tablist" aria-label="Funktion auswählen" className="grid min-w-0 grid-cols-2 border-t border-rich-black/10 lg:block">
            {items.map((item, index) => {
              const selected = index === active;

              return (
                <button
                  key={item.title}
                  id={`feature-tab-${index}`}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  aria-controls="feature-panel"
                  tabIndex={selected ? 0 : -1}
                  onClick={() => setActive(index)}
                  onKeyDown={(event) => {
                    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
                    event.preventDefault();
                    const direction = event.key === "ArrowDown" ? 1 : -1;
                    const next = (index + direction + items.length) % items.length;
                    setActive(next);
                    document.getElementById(`feature-tab-${next}`)?.focus();
                  }}
                  className={cn(
                    "group relative min-w-0 w-full border-b border-rich-black/10 py-3 text-left outline-none motion-safe:transition-colors xl:py-5",
                    "focus-visible:ring-2 focus-visible:ring-smartlane-orange focus-visible:ring-offset-4",
                  )}
                >
                  <span
                    aria-hidden
                    className={cn(
                      "absolute inset-y-4 left-0 w-0.5 origin-center bg-smartlane-orange motion-safe:transition-transform",
                      selected ? "scale-y-100" : "scale-y-0",
                    )}
                  />
                  <span className="grid min-w-0 grid-cols-[auto_minmax(0,1fr)] items-baseline gap-2 pl-3 lg:gap-4 lg:pl-4">
                    <span className={cn("micro-label shrink-0", selected ? "text-smartlane-orange" : "text-rich-black/35")}>
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <span
                      className={cn(
                        "font-heading text-xl uppercase leading-tight md:text-2xl",
                        selected ? "text-rich-black" : "text-rich-black/45 group-hover:text-rich-black/70",
                      )}
                    >
                      {item.shortTitle}
                    </span>
                  </span>
                  <span
                    className={cn(
                      "copy hidden lg:grid overflow-hidden pl-12 text-sm leading-relaxed text-rich-black/65 motion-safe:transition-[grid-template-rows,opacity,margin] motion-safe:duration-300",
                      selected ? "mt-3 grid-rows-[1fr] opacity-100" : "mt-0 grid-rows-[0fr] opacity-0",
                    )}
                  >
                    <span className="min-h-0">{item.desc}</span>
                  </span>
                </button>
              );
            })}
          </div>

          {footer ? (
            <div className="order-last mt-8 min-w-0 lg:order-none lg:col-start-1 lg:row-start-2 lg:mt-10">
              {footer}
            </div>
          ) : null}


          <div
            id="feature-panel"
            role="tabpanel"
            aria-labelledby={`feature-tab-${active}`}
            className="relative min-w-0 overflow-hidden rounded-lg border border-rich-black/15 bg-rich-black p-1.5 shadow-lg"
          >
            <div className="relative aspect-[2/1] overflow-hidden rounded-sm bg-white">
              {items.map((item, index) => (
                <img
                  key={item.title}
                  src={item.image.url}
                  alt={index === active ? item.imageAlt : ""}
                  loading="lazy"
                  decoding="async"
                  aria-hidden={index !== active}
                  className={cn(
                    "absolute inset-0 h-full w-full object-cover object-left-top motion-safe:transition-opacity motion-safe:duration-300",
                    index === active ? "opacity-100" : "opacity-0",
                  )}
                />
              ))}
            </div>
          </div>
          <p className="copy text-sm leading-relaxed text-muted-foreground lg:hidden">{current.desc}</p>
        </div>
      </div>
    </section>
  );
}